import type { ReactNode } from 'react';
import { Star } from 'lucide-react';
import { Card, SectionLabel } from '@wundo/ui';
import { debriefTookPart } from '@wundo/shared/debriefTookPart.ts';
import type { RoleplayDebrief } from '@wundo/shared/roleplay.ts';
import { PracticeNotTakenPart } from './PracticeNotTakenPart.tsx';
import { m } from '@/paraglide/messages.js';

const STARS = [1, 2, 3, 4, 5];

/*
 * A score out of five, as five stars. One image with one label rather than five
 * icons a screen reader reads one by one — "4 van 5" is the whole fact.
 */
function StarRating({ score }: { score: number }) {
  return (
    <span
      role='img'
      aria-label={m.practice_stars({ score })}
      className='flex shrink-0 gap-0.75 pt-0.5'
    >
      {STARS.map((step) => (
        <Star
          key={step}
          aria-hidden='true'
          className={
            step <= score ? 'size-3.25 fill-ink text-ink' : 'size-3.25 text-line'
          }
        />
      ))}
    </span>
  );
}

export interface PracticeDebriefProps {
  debrief: RoleplayDebrief;
  /** The ways on when the user did not take part, for a host that has them. */
  notTakenPartActions?: ReactNode;
}

/**
 * What Wundo made of one practice conversation: the recap, a score and a line
 * per learning objective, the moment that stood out and the one thing to try
 * next time.
 *
 * The same four cards wherever the feedback is read — in the dialog on Oefenen,
 * and on the practice screen itself for someone who came in on a link and has
 * no Oefenen to go back to. One attempt only: which attempt is being read, and
 * how it compares with the last one, is the job of whatever renders this.
 * A practice the user did not take part in has none of the four.
 */
export function PracticeDebrief({ debrief, notTakenPartActions }: PracticeDebriefProps) {
  if (!debriefTookPart(debrief)) {
    return <PracticeNotTakenPart actions={notTakenPartActions} />;
  }

  return (
    <>
      <Card>
        <SectionLabel>{m.practice_feedback_summary()}</SectionLabel>
        <p className='mt-3 text-md leading-relaxed text-ink-body'>{debrief.recap}</p>
      </Card>

      <Card>
        <SectionLabel>{m.practice_feedback_objectives()}</SectionLabel>
        {/*
          One criterion is a heading, a paragraph and a score, and five of them
          under one label read as a single wall unless the blocks are given air:
          58px between them, with the hairline sitting halfway so it separates
          rather than underlines.
        */}
        <ul className='mt-3.5 flex flex-col gap-7.25'>
          {debrief.ratings.map((rating) => (
            <li
              key={rating.objective}
              className='flex items-start gap-4 border-t border-line pt-7.25 first:border-t-0 first:pt-0'
            >
              <span className='min-w-0 flex-1'>
                <span className='block text-md font-semibold text-ink'>
                  {rating.objective}
                </span>
                <span className='mt-1 block text-md text-ink-body'>
                  {rating.feedback}
                </span>
              </span>
              <StarRating score={rating.score} />
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <SectionLabel>{m.practice_feedback_standout()}</SectionLabel>
        <p className='mt-3 text-md leading-relaxed text-ink-body'>{debrief.standout}</p>
      </Card>

      <Card>
        <SectionLabel>{m.practice_feedback_tip()}</SectionLabel>
        <p className='mt-3 text-md leading-relaxed text-ink-body'>{debrief.tip}</p>
      </Card>
    </>
  );
}
